import {
  ExceptionTarget,
  ExceptionTimeWindow,
  ScheduleException,
} from '@weave/schema-gen-ts/dist/schemas/schedule/v3/exception.pb';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  Table,
  TableColumnConfig,
  EditIcon,
  Truncated,
  TrashIcon,
  Chip,
  ChipVariants,
  NakedButton,
  Text,
  formatDate,
  ModalControlTriggerProps,
} from '@frontend/design-system';
import { useSchedulingLocationInfo } from '../../../hooks/use-scheduling-location-info';
import { getAvailabilityRecurrenceText } from '../helpers';
import { FiltersDrawer, type FilterState } from './components/filters-drawer';
import { QuickFilters, type QuickFiltersState } from './components/quick-filters';
import { SortFilter, SortFilterValue } from './components/sort-filter';
import { formatExceptionDateString, formatExceptionTimeRangeString } from './helpers';
import { ResourceChipMeta, ResourceType } from './type';

export type { FilterState } from './components/filters-drawer';

const resourceChipMeta: Record<ResourceType, ResourceChipMeta> = {
  PROVIDER: { variant: 'primary', icon: 'provider' as IconName },
  OPERATORY: { variant: 'warn', icon: 'operatory' as IconName },
  SERVICE: { variant: 'eggplant', icon: 'appointment-type' as IconName },
};

type Props = {
  locationId: string;
  data: ScheduleException[];
  isLoading: boolean;
  editTriggerProps: ModalControlTriggerProps;
  deleteTriggerProps: ModalControlTriggerProps;
  searchValue: string;
  filters: FilterState;
  quickFilters: QuickFiltersState;
  sortBy: SortFilterValue;
  searchHandler: (searchValue: string) => void;
  setSelectedException: (exception: ScheduleException) => void;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  setQuickFilters: (filters: QuickFiltersState) => void;
  setSortBy: (sortBy: SortFilterValue) => void;
};

export const RuleExceptionsTable = ({
  locationId,
  data,
  isLoading,
  editTriggerProps,
  deleteTriggerProps,
  searchValue,
  filters,
  quickFilters,
  sortBy,
  searchHandler,
  setSelectedException,
  setFilters,
  setQuickFilters,
  setSortBy,
}: Props) => {
  const { t } = useTranslation('schedule-settings');
  const { getLocationTimeZone } = useSchedulingLocationInfo();
  const timezone = getLocationTimeZone(locationId);

  const { invalidateRuleExceptions } = SchedulerV3.Helpers.useSchedulerV3InvalidateQueries();

  const statusCellRenderer = (cellValue: string) => {
    let variant = '' as ChipVariants;
    let iconName = '' as IconName;
    switch (cellValue) {
      case 'CURRENTLY_LIVE':
        variant = 'success';
        iconName = 'star';
        break;
      case 'PAST':
        variant = 'neutral';
        iconName = 'block';
        break;
      case 'UPCOMING':
        variant = 'warn';
        iconName = 'pending';
        break;
      default:
        return '-';
    }
    return (
      <Chip variant={variant}>
        <Icon name={iconName} size={16} />
      </Chip>
    );
  };

  const targetCellRenderer = (cellValue?: ExceptionTarget, row?: ScheduleException) => {
    if (!cellValue?.scopeType) return '-';

    switch (cellValue.scopeType) {
      case 'LOCATION':
        return (
          <Chip variant='success' maxWidth='fit-content'>
            <div css={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Icon name='building-small' />
              {t('All Resources')}
            </div>
          </Chip>
        );
      case 'RESOURCE': {
        const resources = cellValue.resources ?? [];
        if (!resources.length) return '-';

        const visibleChips = resources.slice(0, resources.length > 2 ? 1 : 2);
        const remainingResources = resources.length - visibleChips.length;

        return (
          <div css={{ display: 'flex', gap: theme.spacing(1), flexWrap: 'nowrap' }}>
            {visibleChips.map((resource) => {
              const meta = resourceChipMeta[resource.type as ResourceType] ?? {
                variant: 'neutral',
                icon: 'appointment-type',
              };
              const chipLabel = resource.name ?? t('Unknown');
              return (
                <Chip key={resource.id} variant={meta.variant} maxWidth={'140px'}>
                  <div css={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Icon name={meta.icon} size={16} />
                    {chipLabel}
                  </div>
                </Chip>
              );
            })}
            {remainingResources > 0 && (
              <NakedButton
                onClick={() => {
                  if (row) {
                    setSelectedException(row);
                    editTriggerProps.onClick();
                  }
                }}
              >
                <Text size='medium'>
                  +{remainingResources} {t('More')}
                </Text>
              </NakedButton>
            )}
          </div>
        );
      }
      default:
        return '-';
    }
  };

  const timeCellRenderer = (cellValue: ExceptionTimeWindow) => {
    if (cellValue === null) return t('All Day');
    if (!cellValue) return '-';

    const start = formatExceptionTimeRangeString(cellValue.startTime);
    const end = formatExceptionTimeRangeString(cellValue.endTime);
    if (start && end) return `${start} - ${end}`;
    if (start) return start;
    return '-';
  };

  const recurringCellRenderer = (cellValue: string) => {
    switch (cellValue) {
      case 'WEEKLY':
        return t('Weekly');
      case 'SPECIFIC_DATE':
        return t('Specific Date');
      case 'CUSTOM':
        return t('Custom');
      case 'SINGLE_OCCURRENCE':
        return t('Never');
      default:
        return '-';
    }
  };

  const colConfig: TableColumnConfig<ScheduleException>[] = [
    {
      id: 'status',
      Header: t('Status'),
      accessor: 'status',
      width: 94,
      cellRenderer: (cellValue) => statusCellRenderer(cellValue),
    },
    {
      id: 'name',
      Header: t('Exception Name'),
      accessor: 'name',
      minWidth: 150,
      maxWidth: 200,
      cellRenderer: (cellValue) => (cellValue ? <Truncated>{cellValue}</Truncated> : '-'),
    },
    {
      id: 'target',
      Header: t('Exception Target'),
      accessor: 'target',
      width: 300,
      cellRenderer: (cellValue, row) => targetCellRenderer(cellValue, row),
      sortType: (rowA, rowB) => {
        const AResource = rowA.original.target?.resources?.[0];
        const BResource = rowB.original.target?.resources?.[0];
        if (!AResource && !BResource) return 0;
        if (!AResource) return 1;
        if (!BResource) return -1;
        return (AResource.name ?? '').localeCompare(BResource.name ?? '');
      },
    },
    {
      id: 'nextOccurrenceStart',
      Header: t('Next Occurrence Start'),
      accessor: 'nextOccurrenceStart',
      cellRenderer: (cellValue) => formatExceptionDateString(cellValue, timezone) || '-',
    },
    {
      id: 'endDate',
      Header: t('End Date'),
      accessor: 'endDate',
      cellRenderer: (cellValue) => (cellValue ? formatDate(cellValue, 'MMMM D, YYYY') : '-'),
    },
    {
      id: 'time',
      Header: t('Time'),
      accessor: 'time',
      cellRenderer: (cellValue) => timeCellRenderer(cellValue),
    },
    {
      id: 'recurring',
      Header: t('Recurring'),
      accessor: 'recurrenceType',
      cellRenderer: (cellValue) => recurringCellRenderer(cellValue),
    },
    {
      id: 'schedule',
      Header: t('Recurrence Schedule'),
      accessor: 'recurrenceSchedule',
      cellRenderer: (_cellValue, rowData) =>
        rowData ? getAvailabilityRecurrenceText(rowData?.recurrenceRule, rowData?.nextOccurrenceStart) : '',
    },
  ];

  const rowActions = {
    actions: [
      {
        label: t('Edit'),
        Icon: EditIcon,
        onClick: (row: ScheduleException) => {
          setSelectedException(row);
          editTriggerProps.onClick();
        },
      },
      {
        label: t('Delete'),
        Icon: TrashIcon,
        onClick: (row: ScheduleException) => {
          setSelectedException(row);
          deleteTriggerProps.onClick();
        },
      },
    ],
  };

  return (
    <Table
      data={data}
      colConfig={colConfig}
      rowActions={rowActions}
      isLoading={isLoading}
      isPaginated
      clientPaginationConfig={{ defaultRowsPerPage: 50, rowsPerPageOptions: [50, 100, 200] }}
      isRowDisabled={(row: ScheduleException) => row.status === 'PAST'}
      emptyStateConfig={{
        type: 'schedule_alerts',
        header: '',
        description: t('This office is really sticking to their schedule - no exceptions added so far!'),
      }}
      hasGlobalSearch
      globalSearchConfig={{
        initialValue: searchValue,
        searchHandler: (value) => searchHandler(value.trim()),
      }}
      tableActions={[
        {
          Icon: () => <Icon name='update' />,
          label: t('Refetch'),
          type: 'icon',
          onClick: () => invalidateRuleExceptions(locationId),
        },
      ]}
      manualFiltersRender={(modalProps, setShowEnabledFiltersBadge) => {
        const hasActiveFilters = Boolean(
          filters.providers?.length ||
            filters.operatories?.length ||
            filters.serviceTypes?.length ||
            filters.startDate ||
            filters.endDate ||
            filters.view ||
            filters.recurrenceTypes?.length
        );
        // to show blue dot to indicate active filters
        setShowEnabledFiltersBadge(hasActiveFilters);

        return modalProps.show ? (
          <FiltersDrawer
            {...modalProps}
            locationId={locationId}
            filters={filters}
            setFilters={setFilters}
            setShowEnabledFiltersBadge={setShowEnabledFiltersBadge}
          />
        ) : undefined;
      }}
      customToolbarRender={() => (
        <div style={{ flexGrow: 1, display: 'flex', gap: theme.spacing(2), alignItems: 'end' }}>
          <QuickFilters filters={quickFilters} onFiltersChange={setQuickFilters} />
          <SortFilter value={sortBy} onChange={setSortBy} />
        </div>
      )}
    />
  );
};
